import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Modal,
    Stack,
    IconButton,
    Tooltip,
    Container
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import {
    Close as CloseIcon,
    People as PeopleIcon,
    AccessTime as AccessTimeIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    AssignmentOutlined as AssignmentIcon,
} from "@mui/icons-material";

import { candidateApi } from "../../api/candidateApi";
import { jdApi } from "../../api/jobdescriptionApi";
import { ERROR_MESSAGES, getPlanExhaustMessage } from "../../components/common/StatusDictionary";


// ✅ Custom Components
import { formatDate, formatTime } from "../../utils/dateUtils";
import CustomCard from "../../components/common/CustomCard";
import CustomButton from "../../components/common/CustomButton";
import FileUploadModal from "../FileUploadModal";
import CustomSnackBar from "../../components/common/CustomSnackbar";
import CustomHeader from "../../components/common/CustomHeader";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomPagination from "../../components/common/CustomPagination";

const CareersPage = () => {
    const theme = useTheme();
    const [jobs, setJobs] = useState([]);
    const [isdone, SetIsdone] = useState(false)
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);
    const [applyModalOpen, setApplyModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Pagination
    const PAGE_SIZE = 9;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const showSnackbar = (message, severity = "info") =>
        setSnackbar({ open: true, message, severity });

    const closeSnackbar = () =>
        setSnackbar((prev) => ({ ...prev, open: false }));

    // ----------------------------- Fetch Jobs -----------------------------
    const fetchJobs = async (p = 1) => {
        try {
            setLoading(true);
            const response = await jdApi.getJobSeekerJDs({ page: p, page_size: PAGE_SIZE });

            const { status, message, data } = response || {};
            /* 1️⃣ Network error */
            if (status === 0) {
                showSnackbar(
                    "Network error. Please check your internet connection.",
                    "warning"
                );
                setJobs([]);
                return;
            }

            /* 2️⃣ Server error */
            if (status >= 500) {
                showSnackbar(
                    message || "Server error while fetching job listings.",
                    "error"
                );
                setJobs([]);
                return;
            }

            /* 3️⃣ Success — backend returns paginated envelope */
            if (status === 200) {
                if (data && Array.isArray(data.results)) {
                    setJobs(Array.isArray(data?.results) ? data.results : []);
                    setTotal(data?.pagination?.total ?? 0);
                    setTotalPages(data?.pagination?.total_pages ?? 1);
                    setPage(data?.pagination?.page ?? p);
                } else {
                    console.warn("Jobs data is not paginated envelope:", data);
                    setJobs([]);
                }
                return;
            }

            /* 4️⃣ Fallback */
            showSnackbar("Unexpected response from server.", "error");
            setJobs([]);
        } catch (err) {
            console.error("Error fetching jobs:", err);
            showSnackbar(
                "Something went wrong while loading jobs.",
                "error"
            );
            setJobs([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage === page) return;
        setPage(newPage);
        fetchJobs(newPage);
    };

    useEffect(() => {
        fetchJobs(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isdone]);

    // ----------------------------- Handle Apply Success -----------------------------
    const handleApplySuccess = () => {
        setApplyModalOpen(false);
        setSelectedJob(null);
        SetIsdone(!isdone);
        showSnackbar("Resume uploaded successfully!", "success");
    };

    return (
        <Box>
            {/* Header */}
            <CustomHeader
                title="Current Openings"
                subtitle="Explore active job postings and find your next great opportunity."
                icon={<WorkOutlineOutlinedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />}
                breadcrumbs={[
                    { label: "Dashboard", path: "/recruiter/dashboard" },
                    { label: "Careers", path: "/careers" }
                ]}
            />

            {/* Jobs Grid */}
            {loading ? (
                <Container maxWidth="xl" sx={{ mt: 3 }}>
                    <SkeletonLoader type="card" count={6} />
                </Container>
            ) : jobs.length === 0 ? (
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 8, textAlign: "center" }}
                >
                    No job openings available at the moment.
                </Typography>
            ) : (
                <Box>
                    <Grid container spacing={2}>
                        {jobs.map((job, index) => {
                            const accentColors = ["primary", "success", "warning", "info", "error", "secondary"];
                            const currentAccent = accentColors[index % accentColors.length];

                            return (
                                <Grid item xs={12} sm={6} md={4} ml={1} key={job.id}>
                                    <CustomCard
                                        accentColor={currentAccent}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: 140,
                                            width: "340px",
                                            borderRadius: 2,
                                        }}
                                        onClick={() => setSelectedJob(job)}
                                    >
                                        {/* 🔹 Job Title with Arrow */}
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-word",
                                                    lineHeight: 1.2,
                                                    flex: 1,
                                                }}
                                            >
                                                {job.job_title || "Not Specified"}
                                            </Typography>

                                            {/* Tooltip Arrow */}
                                            <Tooltip title="Click here to View " arrow placement="bottom">
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        ml: 1,
                                                        color: "black",
                                                        cursor: "default", // not clickable
                                                        "&:hover": { color: "gray" },
                                                    }}
                                                >
                                                    <ArrowForwardRoundedIcon fontSize="medium" />
                                                </Box>
                                            </Tooltip>
                                        </Stack>

                                        {/* Requisition Code */}
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                            <Tooltip title={`Job Code: ${job.requisition_code || "N/A"}`} arrow placement="top">
                                                <AssignmentIcon fontSize="small" color="action" />
                                            </Tooltip>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                noWrap
                                                sx={{ flex: 1 }}
                                            >
                                                Job ID: {job.requisition_code || "N/A"}
                                            </Typography>
                                        </Stack>

                                        {/* Company */}
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                            <Tooltip title={`Company: ${job.company_name || "Not Specified"}`} arrow placement="top">
                                                <BusinessIcon fontSize="small" color="action" />
                                            </Tooltip>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                noWrap
                                                sx={{ flex: 1 }}
                                            >
                                                {job.company_name || "Not Specified"}
                                            </Typography>
                                        </Stack>

                                        {/* Location */}
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                            <Tooltip title={`Location: ${job.location || "Not Specified"}`} arrow placement="top">
                                                <LocationIcon fontSize="small" color="action" />
                                            </Tooltip>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                noWrap
                                                sx={{ flex: 1 }}
                                            >
                                                {job.location || "Not Specified"}
                                            </Typography>
                                        </Stack>

                                        {/* Posted On */}
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <AccessTimeIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                Posted: {formatDate(job.created_at)}
                                            </Typography>
                                        </Stack>
                                    </CustomCard>
                                </Grid>
                            );
                        })}
                    </Grid>
                    <CustomPagination
                        page={page}
                        totalPages={totalPages}
                        total={total}
                        pageSize={PAGE_SIZE}
                        onPageChange={handlePageChange}
                    />
                </Box>
            )}

            {/* ---------------- Job Detail Modal ---------------- */}
            <Modal
                open={Boolean(selectedJob)}
                onClose={() => setSelectedJob(null)}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 500 },
                        maxHeight: "80vh",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 2,
                        overflowY: "auto",
                    }}
                >
                    {selectedJob && (
                        <>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                            >
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                                >
                                    {selectedJob.job_title || "Not Specified"}
                                </Typography>
                                <IconButton onClick={() => setSelectedJob(null)}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>

                            {/* Job Meta */}
                            <Stack spacing={1} mb={2}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AssignmentIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                        Job Code: {selectedJob.requisition_code || "N/A"}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <BusinessIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                        {selectedJob.company_name || "Not Specified"}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <LocationIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                        {selectedJob.location || "Not Specified"}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AccessTimeIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                        Posted On: {formatDate(selectedJob.created_at)}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <PeopleIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                        {selectedJob.applicants || 0} applicants
                                    </Typography>
                                </Stack>
                            </Stack>

                            {/* Role Overview */}
                            <Typography variant="subtitle1" fontWeight="medium" mb={1} mt={1}>
                                Role Overview
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                {selectedJob.summary || "No description provided."}
                            </Typography>

                            <CustomButton
                                fullWidth
                                onClick={() => setApplyModalOpen(true)}
                                disabled={isSubmitting}
                            >
                                Apply
                            </CustomButton>
                        </>
                    )}
                </Box>
            </Modal>

            {/* ---------------- Apply Modal (Resume Upload) ---------------- */}
            {applyModalOpen && selectedJob && (
                <FileUploadModal
                    open={applyModalOpen}
                    uploadType="resume"
                    apiEndpoint={candidateApi.apply}   // 👈 resume API
                    onClose={() => setApplyModalOpen(false)}
                    setLoading={setIsSubmitting}
                    extraPayload={{ job_id: selectedJob.id, recruiter_id: selectedJob.created_by }}
                    postUploadHandler={() => {
                        handleApplySuccess();
                    }}
                    onError={(error) => {
                        // const errorMessage = error?.planExhaust
                        //     ? getPlanExhaustMessage(error.message)
                        //     : error?.isRawMessage
                        //         ? error.message
                        //         : ERROR_MESSAGES[error?.message] || ERROR_MESSAGES.UnexpectedError || "Application failed.";
                        // // console.log(errorMessage)
                        showSnackbar(error.message, error?.severity || "error");
                    }}
                />
            )}

            {/* ---------------- Snackbar ---------------- */}
            <CustomSnackBar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={closeSnackbar}
                autoHideDuration={6000}
            />
        </Box>
    );
};

export default CareersPage;
